/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { css } from '@emotion/css'
import { useTheme, Theme } from '@emotion/react'
import { t } from 'i18next'
import { VenueBranding } from '@/libs/helpers/adapters'

const iframeDivStyles = (): string => css`
  margin-top: 16px;
`

const iframeFields = (): string => css`
  height: 75px;
  width: 100%;
  margin-top: 4px;
`

const fieldBlock = (theme: Theme): string => css`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${theme.breakpoints.SM}) {
    display: block;
  }
`

const flexBlockItemExpiration = (theme: Theme): string => css`
  width: 146px;

  @media (max-width: ${theme.breakpoints.SM}) {
    width: 100%;
  }
`

const flexBlockItemCVV = (theme: Theme): string => css`
  width: 114px;

  @media (max-width: ${theme.breakpoints.SM}) {
    width: 100%;
  }
`

interface PayrixIframeProps {
  payrixSDKLoaded: boolean
  venueBranding?: VenueBranding
  divID?: string
}

const PayrixIframe: FunctionComponent<PayrixIframeProps> = ({
  payrixSDKLoaded,
  venueBranding,
  divID = 'payrix-credit-card-iframe',
}) => {
  const theme = useTheme()
  const refName = useRef<HTMLDivElement>(null)
  const [showSpinner, setShowSpinner] = useState(true)

  useEffect(() => {
    if (payrixSDKLoaded) {
      PayFields.fields = [
        { type: 'name', element: '#name' },
        { type: 'number', element: '#number' },
        { type: 'cvv', element: '#cvv' },
        { type: 'expiration', element: '#expiration' },
      ]

      PayFields.customizations.placeholders = {
        '#name': t('payment.placeholders.cardholder'),
        '#payment_number': '0000 0000 0000 0000',
        '#payment_cvv': t('payment.placeholders.cvv'),
        '#expiration': t('payment.placeholders.expiration'),
      }

      const borderColor = venueBranding ? venueBranding.secondaryColor : '#E5E5E5'

      PayFields.customizations.style = {
        '.input': {
          'box-sizing': 'border-box',
          width: '100%',
          height: '44px',
          border: `1px solid ${borderColor}`,
          borderRadius: '4px',
          margin: '0 2px',
          padding: '8px',
          position: 'relative',
          left: '-2px',
        },
        '.card-icon': {
          top: '6px',
          display: 'none',
        },
      }

      PayFields.appendIframe()
      setTimeout(() => {
        // If fields aren't appended after 1 seconds, force fields to be rendered
        if (!PayFields.appended) {
          PayFields.addFields()
        }
      }, 1000)
    }
  }, [payrixSDKLoaded])

  const iframeLoader = (): string => css`
    border: 4px solid ${venueBranding ? venueBranding.secondaryColor : '#f3f3f3'}; /* Light grey */
    border-top: 4px solid ${venueBranding ? venueBranding.primaryColor : '#3498db'}; /* Blue */
    border-radius: 50%;
    width: 44px;
    height: 44px;
    animation: spin 2s linear infinite;

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  `

  useEffect(() => {
    // Observe the card name field
    const node = refName.current

    // Define a function to observe changes to the node's children
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Stop showing the spinner once a field as been added
          setShowSpinner(false)
        }
      }
    })

    // Start observing the node's children
    observer.observe(node as Node, { childList: true })

    // Clean up the observer when component unmounts
    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div id={divID} className={iframeDivStyles()}>
      <label>{t('payment.labels.name')}</label>
      <div id="name" ref={refName} className={iframeFields()}>
        {showSpinner && <div className={iframeLoader()}></div>}
      </div>
      <label>{t('payment.labels.number')}</label>
      <div id="number" className={iframeFields()}>
        {showSpinner && <div className={iframeLoader()}></div>}
      </div>
      <div className={fieldBlock(theme)}>
        <div className={flexBlockItemExpiration(theme)}>
          <label>{t('payment.labels.expiration')}</label>
          <div id="expiration" className={iframeFields()}>
            {showSpinner && <div className={iframeLoader()}></div>}
          </div>
        </div>
        <div className={flexBlockItemCVV(theme)}>
          <label>{t('payment.labels.cvv')}</label>
          <div id="cvv" className={iframeFields()}>
            {showSpinner && <div className={iframeLoader()}></div>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PayrixIframe
